import React, { useState, useEffect, useCallback } from "react";
import Form from "react-bootstrap/Form";
import _ from "lodash";
import uuid from "react-uuid";
import Loading from "../../../common/components/loading/loading";
import { getCommodityClasses } from "../../../common/services/products/commodityClassService";
import SearchBox from "../../../common/components/search/searchBox";
import CommodityClassesTable from "./commodityClassesTable";
import { useTranslation } from "react-i18next";

const CommodityClasses = ({ onClick, allowEdit }) => {
  const { t } = useTranslation(["products"]);
  const [commodityClasses, setCommodityClasses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [sortColumn, setSortColumn] = useState({ path: "name", order: "asc" });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch commodity classes on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getCommodityClasses();
        setCommodityClasses(data);
      } catch (error) {
        console.error("Error fetching commodity classes:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle commodity class updates
  const handleCommodityClassUpdated = useCallback((commodityClass) => {
    setCommodityClasses((prevClasses) => {
      const updatedClasses = [...prevClasses];
      const index = updatedClasses.findIndex(
        (u) => u.commodityClassId === commodityClass.commodityClassId
      );
      if (index > -1) {
        updatedClasses.splice(index, 1);
      }
      updatedClasses.push(commodityClass);
      return updatedClasses;
    });
  }, []);

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  // Handle sorting
  const handleSort = (colId, sort) => {
    if (colId && sort) {
      setSortColumn({
        path: colId,
        order: sort,
      });
    }
  };

  // Handle active toggle
  const handleToggleActiveOnly = () => {
    setActiveOnly(!activeOnly);
  };

  // Handle commodity class selection
  const handleCommodityClassSelected = (commodityClass, isNew = false) => {
    // Inject the callback function before calling back to the onClick event
    onClick(commodityClass, handleCommodityClassUpdated, isNew);
  };

  // Get filtered and sorted data
  const getFilteredData = () => {
    let filtered = commodityClasses;

    if (activeOnly) filtered = filtered.filter((u) => u.active);

    if (searchQuery)
      filtered = filtered.filter(
        (u) =>
          (u.name != null &&
            u.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (u.shortName != null &&
            u.shortName.toLowerCase().includes(searchQuery.toLowerCase()))
      );

    const sorted = _.orderBy(filtered, [sortColumn.path], [sortColumn.order]);

    return { totalCount: filtered.length, data: sorted };
  };

  if (isLoading) return <Loading />;

  const { totalCount, data: filteredCommodityClasses } = getFilteredData();

  const newCommodityClass = {
    commodityClassId: uuid(),
    name: t("NewCommodityClass"),
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col">
          <SearchBox value={searchQuery} onChange={handleSearch} />
        </div>
        <div className="col">
          <Form.Switch
            className="switch"
            type="switch"
            label={t("ActiveOnly")}
            checked={activeOnly}
            value={activeOnly}
            onChange={handleToggleActiveOnly}
          />
        </div>
        <div className="col">
          {allowEdit && (
            <button
              onClick={() =>
                handleCommodityClassSelected(newCommodityClass, true)
              }
              className="btn btn-sm btn-primary float-sm-right"
            >
              {t("AddCommodityClass")}
            </button>
          )}
        </div>
      </div>
      <p>{t("RetrievedCommodityClass", { count: totalCount })}</p>
      <CommodityClassesTable
        commodityClasses={filteredCommodityClasses}
        onSort={handleSort}
        onRowClick={handleCommodityClassSelected}
      />
    </div>
  );
};

export default CommodityClasses;
